import { createBrowserRouter } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { RootLayout } from './layouts/RootLayout'
import { AuthLayout } from './layouts/AuthLayout'
import { TerminalLayout } from './layouts/TerminalLayout'
import { LoadingSpinner } from './components/ui/LoadingSpinner'
import { ProGate } from './components/ui/ProGate'
import { TierGate } from './components/ui/TierGate'

// Lazy-loaded pages
const TerminalPage      = lazy(() => import('./pages/TerminalPage'))
const WatchlistPage     = lazy(() => import('./pages/WatchlistPage'))
const SettingsPage      = lazy(() => import('./pages/SettingsPage'))
const SupplyChainPage   = lazy(() => import('./pages/SupplyChainPage'))
const MessagesPage      = lazy(() => import('./pages/MessagesPage'))
const GeopoliticalPage  = lazy(() => import('./pages/GeopoliticalPage'))
const EarningsPage          = lazy(() => import('./pages/EarningsPage'))
const OptionsPage           = lazy(() => import('./pages/OptionsPage'))
const BacktestPage          = lazy(() => import('./pages/BacktestPage'))
const AIStrategyLabPage     = lazy(() => import('./pages/AIStrategyLabPage'))
const PortfolioOptimizerPage = lazy(() => import('./pages/PortfolioOptimizerPage'))
const InstitutionalPage     = lazy(() => import('./pages/InstitutionalPage'))
const EconomicCalendarPage  = lazy(() => import('./pages/EconomicCalendarPage'))
const MacroPage             = lazy(() => import('./pages/MacroPage'))
const LandingPage    = lazy(() => import('./pages/LandingPage'))
const LoginPage      = lazy(() => import('./pages/auth/LoginPage'))
const CallbackPage   = lazy(() => import('./pages/auth/CallbackPage'))
const PortfolioSimulatorPage = lazy(() => import('./pages/PortfolioSimulatorPage'))
const DemoPage               = lazy(() => import('./pages/DemoPage'))
const PrivacyPage      = lazy(() => import('./pages/legal/PrivacyPage'))
const TermsPage        = lazy(() => import('./pages/legal/TermsPage'))
const DisclaimerPage   = lazy(() => import('./pages/legal/DisclaimerPage'))
const UnsubscribePage  = lazy(() => import('./pages/legal/UnsubscribePage'))
const DoNotSellPage    = lazy(() => import('./pages/legal/DoNotSellPage'))
const TrackRecordPage  = lazy(() => import('./pages/TrackRecordPage'))
const SharePage        = lazy(() => import('./pages/SharePage'))
const DividendsPage    = lazy(() => import('./pages/DividendsPage'))
const ScreenerPage     = lazy(() => import('./pages/ScreenerPage'))
const SEC10KPage             = lazy(() => import('./pages/SEC10KPage'))
const RiskIntelligencePage   = lazy(() => import('./pages/RiskIntelligencePage'))
const EquityResearchPage     = lazy(() => import('./pages/EquityResearchPage'))
const CryptoPage             = lazy(() => import('./pages/CryptoPage'))
const SocialPage             = lazy(() => import('./pages/SocialPage'))
const CongressPage           = lazy(() => import('./pages/CongressPage'))
const DarkPoolPage           = lazy(() => import('./pages/DarkPoolPage'))
const IPOPage                = lazy(() => import('./pages/IPOPage'))
const SectorRotationPage     = lazy(() => import('./pages/SectorRotationPage'))
const AdminPage              = lazy(() => import('./pages/AdminPage'))
const MultiChartPage         = lazy(() => import('./pages/MultiChartPage'))
const BotPage                = lazy(() => import('./pages/BotPage'))
const GeoRiskPage            = lazy(() => import('./pages/GeoRiskPage'))
// Hub pages
const RiskHubPage            = lazy(() => import('./pages/RiskHubPage'))
const PortfolioHubPage       = lazy(() => import('./pages/PortfolioHubPage'))
const FlowsHubPage           = lazy(() => import('./pages/FlowsHubPage'))
const ResearchHubPage        = lazy(() => import('./pages/ResearchHubPage'))
const EventsHubPage          = lazy(() => import('./pages/EventsHubPage'))
const SectorsHubPage         = lazy(() => import('./pages/SectorsHubPage'))
const CalculatorsPage        = lazy(() => import('./pages/CalculatorsPage'))
const SeasonalityPage        = lazy(() => import('./pages/SeasonalityPage'))
const UserGuidePage          = lazy(() => import('./pages/UserGuidePage'))

/** Wrap a free-tier page in a Suspense boundary. */
const wrap = (el: React.ReactNode) => (
  <Suspense fallback={<LoadingSpinner />}>{el}</Suspense>
)

/** Gate a feature behind the Pro plan ($49/mo). */
const pro = (feature: string, el: React.ReactNode) => (
  <Suspense fallback={<LoadingSpinner />}>
    <ProGate feature={feature}>{el}</ProGate>
  </Suspense>
)

/** Gate a feature behind the Pro+ plan ($99/mo). */
const proPlus = (feature: string, el: React.ReactNode) => (
  <Suspense fallback={<LoadingSpinner />}>
    <TierGate feature={feature} minTier="pro_plus">{el}</TierGate>
  </Suspense>
)

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Auth routes (public)
      {
        element: <AuthLayout />,
        children: [
          { path: '/login',         element: wrap(<LoginPage />) },
          { path: '/auth/callback', element: wrap(<CallbackPage />) },
        ],
      },
      // Public routes
      { path: '/',     element: wrap(<LandingPage />) },
      { path: '/demo', element: wrap(<DemoPage />) },

      // App routes (protected by TerminalLayout auth check)
      {
        element: <TerminalLayout />,
        children: [
          // ── Free tier ───────────────────────────────────────────────────
          { path: '/terminal',     element: wrap(<TerminalPage />) },
          { path: '/portfolio',    element: wrap(<PortfolioHubPage />) },
          { path: '/watchlist',    element: wrap(<WatchlistPage />) },
          { path: '/events',       element: wrap(<EventsHubPage />) },
          { path: '/sectors',      element: wrap(<SectorsHubPage />) },
          { path: '/macro',        element: wrap(<MacroPage />) },
          { path: '/social',       element: wrap(<SocialPage />) },
          { path: '/screener',     element: wrap(<ScreenerPage />) },
          { path: '/messages',     element: wrap(<MessagesPage />) },
          { path: '/settings',     element: wrap(<SettingsPage />) },
          { path: '/admin',        element: wrap(<AdminPage />) },

          // ── Pro tier ($49/mo) ─────────────────────────────────────────
          { path: '/multi-chart',  element: pro('Multi-Symbol Comparison', <MultiChartPage />) },
          { path: '/options',      element: pro('Options Chain & Volatility Surface', <OptionsPage />) },
          { path: '/research',     element: pro('Research Hub', <ResearchHubPage />) },
          { path: '/flows',        element: pro('Flows Hub', <FlowsHubPage />) },
          { path: '/risk',         element: pro('Risk Hub', <RiskHubPage />) },
          { path: '/bot',          element: pro('Strategy Ideas', <BotPage />) },

          // ── Backwards-compat aliases (not in nav) ────────────────────
          { path: '/sector',           element: wrap(<SectorsHubPage />) },
          { path: '/earnings',         element: wrap(<EarningsPage />) },
          { path: '/calendar',         element: wrap(<EconomicCalendarPage />) },
          { path: '/dividends',        element: wrap(<DividendsPage />) },
          { path: '/simulator',        element: wrap(<PortfolioSimulatorPage />) },
          { path: '/crypto',           element: wrap(<CryptoPage />) },
          { path: '/backtest',         element: pro('Strategy Backtesting', <BacktestPage />) },
          { path: '/strategy-lab',     element: pro('AI Strategy Lab', <AIStrategyLabPage />) },
          { path: '/optimizer',        element: pro('Portfolio Optimizer', <PortfolioOptimizerPage />) },
          { path: '/institutional',    element: pro('Institutional Flow Tracker', <InstitutionalPage />) },
          { path: '/sec',              element: pro('SEC 10-K AI Reader', <SEC10KPage />) },
          { path: '/equity-research',  element: pro('Equity Research Reports', <EquityResearchPage />) },
          { path: '/risk-intelligence',element: pro('Risk Intelligence', <RiskIntelligencePage />) },
          { path: '/geo-risk',         element: pro('Geospatial Risk Map', <GeoRiskPage />) },
          { path: '/congress',         element: pro('Congress Trades Tracker', <CongressPage />) },
          { path: '/ipo',              element: pro('IPO Calendar', <IPOPage />) },
          { path: '/supply-chain',     element: proPlus('Supply Chain Intelligence', <SupplyChainPage />) },
          { path: '/geopolitical',     element: proPlus('Geopolitical Risk Monitor', <GeopoliticalPage />) },
          { path: '/dark-pool',        element: proPlus('Dark Pool & Options Flow', <DarkPoolPage />) },
          { path: '/sector-rotation',  element: proPlus('Sector Rotation Signals', <SectorRotationPage />) },
          { path: '/calculators',      element: wrap(<CalculatorsPage />) },
          { path: '/seasonality',      element: wrap(<SeasonalityPage />) },
          { path: '/guide',            element: wrap(<UserGuidePage />) },
        ],
      },
      // Public marketing/trust pages
      { path: '/track-record', element: wrap(<TrackRecordPage />) },
      // Legal (public)
      { path: '/privacy',      element: wrap(<PrivacyPage />) },
      { path: '/terms',        element: wrap(<TermsPage />) },
      { path: '/disclaimer',   element: wrap(<DisclaimerPage />) },
      { path: '/unsubscribe',  element: wrap(<UnsubscribePage />) },
      { path: '/do-not-sell',  element: wrap(<DoNotSellPage />) },
      { path: '/share/:token', element: wrap(<SharePage />) },
    ],
  },
])
